import React, { FC } from 'react';

import { Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import VideoClipIcon from '../../../../assets/icons/VideoClipIcon';
import { CardLabels } from '../../../../components/Card';
import { CategoryLabel } from '../../../../components/UI/CategoryLabel';
import { DEFAULT_DISCOVER_URL } from '../../../../const';
import styled, { css } from '../../../../styled';
import { SMALL_MARGIN, SMALL_MARGIN_PX } from '../../../../theme';

interface GlossaryHitProps {
  slug: string;
  title: string;
  description: string;
  categories?: [any];
  videoUrl?: string;
  handleClickHit: () => void;
}

const GlossaryHit: FC<GlossaryHitProps> = ({
  slug,
  title,
  description,
  categories,
  videoUrl,
  handleClickHit,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();

  const croppedDescription = description ? description.substring(0, 299) : '';

  return (
    <Wrapper>
      <CardLabels>
        <CategoryLabel
          className="cardLabel"
          name={t('page.discover.glossary')}
          textColor={theme.palette.common.blueDark}
          bgColor={theme.palette.common.yellow}
          Icon={videoUrl ? StyledVideoClipIcon : undefined}
        />
        {categories &&
          categories.map((category) => (
            <CategoryLabel
              className="cardLabel"
              key={category?.id}
              name={category?.title}
              withDots={false}
            />
          ))}
      </CardLabels>
      <Link to={`${DEFAULT_DISCOVER_URL}/glossary/${slug}`} onClick={handleClickHit}>
        <Title variant="h3">{title}</Title>
      </Link>
      <Description optionalStyles={description?.length > 300 && generatedWithDots}>
        {croppedDescription}
      </Description>
      <StyledLink to={`${DEFAULT_DISCOVER_URL}/glossary/${slug}`} onClick={handleClickHit}>
        {t('page.discover.readMore')}
      </StyledLink>
    </Wrapper>
  );
};

export default GlossaryHit;

const StyledVideoClipIcon = styled(VideoClipIcon)`
  color: ${({ theme }) => theme.palette.common.blueDark};
`;

const Wrapper = styled('div')`
  & .cardLabel {
    margin-bottom: ${SMALL_MARGIN / 2}px;

    ${({ theme }) => theme.breakpoints.up('md')} {
      margin-bottom: ${SMALL_MARGIN_PX};
    }
  }
`;
const Title = styled(Typography)`
  && {
    color: ${({ theme }) => theme.palette.common.white};
    font-weight: bold;
    font-size: 24px;
    line-height: 28px;
    margin-bottom: ${SMALL_MARGIN / 2}px;

    ${({ theme }) => theme.breakpoints.up('md')} {
      font-size: 28px;
      line-height: 34px;
      margin-bottom: ${SMALL_MARGIN_PX};
    }
  }
`;
const Description = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'optionalStyles',
})`
  white-space: pre-wrap;
  color: ${({ theme }) => theme.palette.common.gray};
  margin-bottom: ${SMALL_MARGIN / 2}px;
  font-size: 18px;
  line-height: 24px;

  ${({ theme }) => theme.breakpoints.up('md')} {
    font-size: 20px;
    line-height: 30px;
    margin-bottom: ${SMALL_MARGIN_PX};
  }

  ${({ optionalStyles }) => optionalStyles}
`;
const generatedWithDots = css`
  &:after {
    content: '...';
  }
`;
const StyledLink = styled(Link)`
  color: ${({ theme }) => theme.palette.common.violet};
  font-size: 18px;
  line-height: 24px;

  ${({ theme }) => theme.breakpoints.up('md')} {
    font-size: 20px;
    line-height: 30px;
  }

  &:hover {
    text-decoration: underline;
  }
`;
